import { Link } from "react-router-dom";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { FadeUp, Eyebrow, MaskedLines } from "@/components/Reveal";
import { PARTNERS, SITE } from "@/data/content";

export default function Partners() {
    return (
        <>
            <Seo
                title="Partners — Builders, Lenders, Agents, Suppliers | Dagaz Development"
                description="Cooperation with licensed builders, architects, engineers, suppliers, lenders and real estate agents across the US and Canada. Ready clients, designed projects, direct contracts."
                path="/partners"
                jsonLd={[
                    orgSchema,
                    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Partners", path: "/partners" }]),
                    {
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        name: "Partner with Dagaz Development",
                        about: { "@type": "Organization", name: SITE.name },
                    },
                ]}
            />
            <PageHeader
                eyebrow="Partners — Cooperation"
                titleLines={["Good projects", "need good hands."]}
                answer="Dagaz Development cooperates with licensed builders, architects and engineers, suppliers, lenders and real estate agents across the United States and Canada. We bring designed projects and ready clients; partners build, supply, finance and sell — with direct contracts and one accountable table."
                crumbs={[{ name: "Partners" }]}
            />

            <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-chalk" data-testid="partner-tracks">
                {PARTNERS.tracks.map((t, i) => (
                    <FadeUp key={t.name} delay={0.03 * i}>
                        <div className="grid lg:grid-cols-12 gap-6 py-14 border-t border-graphite/15">
                            <div className="lg:col-span-1">
                                <span className="font-sans text-[11px] font-medium tracking-[0.28em] text-gold">{String(i + 1).padStart(2, "0")}</span>
                            </div>
                            <div className="lg:col-span-4">
                                <h2 className="font-serif font-light text-3xl md:text-4xl text-graphite">{t.name}</h2>
                                <p className="mt-3 font-sans text-[11px] font-medium tracking-[0.2em] uppercase text-slate">{t.tag}</p>
                            </div>
                            <div className="lg:col-span-6 lg:col-start-7">
                                <p className="font-sans text-base leading-relaxed text-graphite/75">{t.body}</p>
                                <ul className="mt-6 space-y-3">
                                    {t.points.map((p) => (
                                        <li key={p} className="flex items-baseline gap-4 font-sans text-sm text-graphite/80">
                                            <span className="w-6 h-px bg-gold shrink-0 translate-y-[-4px]" aria-hidden="true" />
                                            {p}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </FadeUp>
                ))}
                <div className="border-t border-graphite/15" />
            </section>

            <section className="bg-forest bg-noise px-6 md:px-12 lg:px-16 py-28 md:py-36" data-testid="partner-process">
                <Eyebrow dark className="mb-8">How Partnership Starts</Eyebrow>
                <MaskedLines
                    lines={["One call.", "One pilot. One area."]}
                    inView
                    className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-chalk"
                />
                <div className="mt-16 grid md:grid-cols-3 gap-x-8 gap-y-12 border-t border-chalk/15 pt-12">
                    {PARTNERS.process.map((s, i) => (
                        <FadeUp key={s.title} delay={0.08 * i}>
                            <p className="font-serif font-light text-5xl text-chalk/10">{String(i + 1).padStart(2, "0")}</p>
                            <h3 className="mt-4 font-serif text-2xl text-chalk">{s.title}</h3>
                            <p className="mt-3 font-sans text-sm leading-relaxed text-fog">{s.body}</p>
                        </FadeUp>
                    ))}
                </div>
                <FadeUp delay={0.2} className="mt-16 max-w-2xl">
                    <p className="font-sans text-sm leading-relaxed text-fog border-l-2 border-gold pl-6">
                        We publish no partner names or logos without written consent — usually after five delivered customers. Your reputation is treated like our own.
                    </p>
                </FadeUp>
            </section>

            <section className="px-6 md:px-12 lg:px-16 py-28 md:py-40 bg-chalk" data-testid="partner-cta">
                <div className="max-w-3xl">
                    <MaskedLines
                        lines={["Introduce yourself."]}
                        inView
                        className="font-serif font-light text-5xl md:text-7xl leading-[0.98] tracking-tight text-graphite"
                    />
                    <FadeUp delay={0.25} className="mt-8">
                        <p className="font-sans text-base md:text-lg leading-relaxed text-slate max-w-xl">
                            Tell us your trade, your territory and your best work. Every introduction is read personally and answered within two business days.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.4} className="mt-10 flex flex-wrap items-center gap-8">
                        <Link
                            to="/contact"
                            data-testid="partner-cta-contact"
                            className="group inline-flex items-center gap-3 px-8 py-4 bg-forest text-chalk font-sans text-xs font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-graphite"
                        >
                            Contact Us
                            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                        </Link>
                        <a href={`mailto:${SITE.email}`} data-testid="partner-cta-email" className="link-rule font-sans text-xs font-medium tracking-[0.22em] uppercase text-graphite">
                            {SITE.email}
                        </a>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
